// src/pages/ForgotPassword.jsx
import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  InputAdornment,
  Paper,
  Avatar,
  alpha,
  useTheme,
} from "@mui/material";

import LockResetIcon from "@mui/icons-material/LockReset";
import EmailIcon from "@mui/icons-material/Email";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import { auth } from "../contexts/firebase/config";
import { sendPasswordResetEmail } from "firebase/auth";
import sintanetLogo from "../assets/sintanet-logo.png";

export default function ForgotPassword() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const orange = "#ff6a00";
  const dark = "#07090c";

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email.trim()) {
      setError("Por favor, insira o e-mail associado à sua conta.");
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email.trim());

      setMessage(
        "Instruções enviadas! Verifique o seu e-mail para redefinir a palavra-passe."
      );
    } catch (err) {
      console.error("Erro ao enviar e-mail de recuperação:", err);

      if (err.code === "auth/user-not-found") {
        setError("Nenhuma conta encontrada com este e-mail.");
      } else if (err.code === "auth/invalid-email") {
        setError("Formato de e-mail inválido.");
      } else {
        setError("Falha ao enviar instruções. Tente novamente mais tarde.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 64,
      borderRadius: "18px",
      bgcolor: "#ffffff",
      fontWeight: 700,
      boxShadow: "0 16px 34px rgba(15,23,42,0.06)",
      transition: "0.22s ease",

      "& fieldset": {
        borderColor: "rgba(15,23,42,0.14)",
      },

      "&:hover fieldset": {
        borderColor: alpha(orange, 0.6),
      },

      "&.Mui-focused": {
        boxShadow: `0 0 0 4px ${alpha(orange, 0.13)}, 0 18px 40px rgba(15,23,42,0.10)`,
      },

      "&.Mui-focused fieldset": {
        borderColor: orange,
      },
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: orange,
    },
  };
    return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr" },
        bgcolor: "#f8fafc",
        overflow: "hidden",
      }}
    >
      {/* LADO ESQUERDO */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          p: 7,
          overflow: "hidden",
          background: `
            radial-gradient(circle at top right, rgba(255,106,0,0.25), transparent 30%),
            linear-gradient(180deg, #05070a 0%, #0b0f14 100%)
          `,
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Box
            component="img"
            src={sintanetLogo}
            alt="Sintanet"
            sx={{
              width: 325,
              objectFit: "contain",
              mb: 8,
              filter: "drop-shadow(0 18px 35px rgba(0,0,0,0.45))",
            }}
          />

          <Typography
            sx={{
              fontSize: "4.2rem",
              lineHeight: 0.95,
              fontWeight: 950,
              letterSpacing: "-0.08em",
              color: "#ffffff",
              maxWidth: 520,
            }}
          >
            Recupere
            <br />

            <Box
              component="span"
              sx={{
                color: orange,
              }}
            >
              o acesso
            </Box>

            <br />
            à sua conta
          </Typography>

          <Typography
            sx={{
              mt: 3,
              maxWidth: 500,
              color: "rgba(255,255,255,0.72)",
              fontSize: "1.08rem",
              lineHeight: 1.8,
            }}
          >
            Introduza o e-mail associado à sua conta e enviaremos um link seguro
            para redefinir a sua palavra-passe.
          </Typography>
        </Box>

        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "28px",
              bgcolor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
              backdropFilter: "blur(14px)",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 58,
                  height: 58,
                  bgcolor: alpha(orange, 0.18),
                  color: orange,
                }}
              >
                <ShieldRoundedIcon />
              </Avatar>

              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 900,
                    fontSize: "1.05rem",
                  }}
                >
                  Segurança avançada
                </Typography>

                <Typography
                  sx={{
                    mt: 0.4,
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.6,
                  }}
                >
                  Todos os pedidos de recuperação são protegidos e verificados.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: -120,
            bottom: -120,
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              orange,
              0.45
            )} 0%, transparent 72%)`,
            filter: "blur(12px)",
          }}
        />
      </Box>

      {/* LADO DIREITO */}
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          px: { xs: 2.5, sm: 5, lg: 7 },
          py: { xs: 4, lg: 6 },
          position: "relative",
        }}
      >
        <Button
          component={RouterLink}
          to="/login"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{
            position: "absolute",
            top: { xs: 18, md: 32 },
            right: { xs: 18, md: 40 },
            textTransform: "none",
            fontWeight: 900,
            color: dark,
          }}
        >
          Voltar ao login
        </Button>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 720,
            borderRadius: { xs: "30px", md: "42px" },
            p: { xs: 3, sm: 5, md: 6 },
            bgcolor: "#ffffff",
            border: "1px solid rgba(15,23,42,0.08)",
            boxShadow: "0 30px 80px rgba(15,23,42,0.10)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: -120,
              top: -120,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(
                orange,
                0.18
              )} 0%, transparent 70%)`,
            }}
          />

          <Stack
            spacing={3}
            alignItems="center"
            sx={{ position: "relative", zIndex: 2 }}
          >
            <Avatar
              sx={{
                width: 112,
                height: 112,
                bgcolor: alpha(orange, 0.12),
                color: orange,
                border: `1px solid ${alpha(orange, 0.18)}`,
              }}
            >
              <LockResetIcon sx={{ fontSize: 58 }} />
            </Avatar>

            <Box textAlign="center">
              <Typography
                sx={{
                  fontSize: { xs: "2rem", md: "3rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.07em",
                  color: dark,
                  lineHeight: 1,
                }}
              >
                Recuperar palavra-passe
              </Typography>

              <Typography
                sx={{
                  mt: 2,
                  color: "text.secondary",
                  lineHeight: 1.8,
                  fontSize: "1.02rem",
                  maxWidth: 520,
                }}
              >
                Introduza o e-mail associado à sua conta Sintanet para receber
                um link de recuperação seguro.
              </Typography>
            </Box>
                        {message && (
              <Alert
                severity="success"
                sx={{
                  width: "100%",
                  borderRadius: "18px",
                }}
              >
                {message}
              </Alert>
            )}

            {error && (
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  borderRadius: "18px",
                }}
              >
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
              }}
            >
              <Stack spacing={2.5}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemplo@empresa.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: orange }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />

                <Paper
                  elevation={0}
                  sx={{
                    p: 2.3,
                    borderRadius: "20px",
                    bgcolor: alpha(orange, 0.07),
                    border: `1px solid ${alpha(orange, 0.18)}`,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: orange,
                        color: "#fff",
                      }}
                    >
                      <InfoRoundedIcon />
                    </Avatar>

                    <Typography
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.55,
                        fontWeight: 600,
                      }}
                    >
                      Enviaremos um email com um link seguro para redefinir a sua
                      palavra-passe.
                    </Typography>
                  </Stack>
                </Paper>

                <Button
                  type="submit"
                  disabled={loading}
                  startIcon={!loading ? <MarkEmailReadRoundedIcon /> : null}
                  endIcon={!loading ? <ArrowForwardRoundedIcon /> : null}
                  sx={{
                    height: 66,
                    borderRadius: "18px",
                    textTransform: "none",
                    fontWeight: 950,
                    fontSize: "1.08rem",
                    color: "#fff",
                    background: `linear-gradient(90deg, ${orange}, #ff4d00)`,
                    boxShadow: `0 18px 36px ${alpha(orange, 0.35)}`,
                    transition: "0.22s ease",

                    "&:hover": {
                      transform: "translateY(-2px)",
                      background: `linear-gradient(90deg, #ff7a1a, ${orange})`,
                      boxShadow: `0 24px 46px ${alpha(orange, 0.42)}`,
                    },

                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.8)",
                      background: "#fbbf24",
                      boxShadow: "none",
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Enviar instruções"
                  )}
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ width: "100%" }} />

            <Typography
              sx={{
                color: "text.secondary",
                textAlign: "center",
              }}
            >
              Lembrou-se da sua palavra-passe?{" "}
              <RouterLink to="/login" style={{ textDecoration: "none" }}>
                <Box
                  component="span"
                  sx={{
                    color: orange,
                    fontWeight: 950,
                  }}
                >
                  Iniciar sessão
                </Box>
              </RouterLink>
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{
                color: "text.secondary",
              }}
            >
              <ShieldRoundedIcon fontSize="small" />
              <Typography fontSize={14}>
                Os seus dados estão protegidos e encriptados.
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}